<template>
    <div class="container container--budget">  

        <Loader v-if="!ready || processing"></Loader>

        <article class="budget" v-if="ready">

            <header class="budget__heading">
                <h1 class="woocommerce-order-details__title" v-if="isNew">Créer un nouveau budget</h1>
                <h1 class="woocommerce-order-details__title" v-if="!isNew">Modifier le budget <span v-if="budget.code">n° {{ budget.code }}</span></h1>
            </header> 

            <Edit @submit.native="checkForm">

                <Message v-if="Object.values(this.errors).length" type="important">
                    <ul>
                        <li v-for="(message, index) in Object.values(this.errors)" :key="index">{{ message }}</li>
                    </ul>
                </Message>

                <fieldset class="edit__fieldset">
                    <div class="edit__field edit__field--main"
                        :class="{ 'error': errors.name }"> 
                        <label class="edit__field__label" for="name">Libellé</label>
                        <input class="edit__field__input"
                            type="text"
                            id="name"
                            v-model="budget.libelle"
                            placeholder="Libellé" 
                            :maxlength="255"/>
                    </div>

                    <div class="edit__field"
                        :class="{ 'error': errors.validityFrom || errors.validityTo }"> 
                        <label class="edit__field__label" for="validityFrom">Valide du</label>
                        <Datepicker class="edit__field__input"
                            id="validityFrom"
                            :language="datepickerLocale"
                            :monday-first="datepickerMondayFirst"
                            v-model="budget.validiteStartAt"
                            placeholder="jj/mm/aaaa" />
                        <label class="edit__field__label" for="validityTo"> au </label>
                        <Datepicker class="edit__field__input"
                            id="validityTo"
                            :language="datepickerLocale"
                            :monday-first="datepickerMondayFirst"
                            v-model="budget.validiteEndAt"
                            placeholder="jj/mm/aaaa" />
                    </div>

                    <div class="edit__field"> 
                        <label class="edit__field__label" for="amount">Montant</label>
                        <input class="edit__field__input"
                            type="number"
                            id="amount"
                            v-model="budget.amount"
                            min="0" />
                        <span class="edit__field__suffix">€</span>
                    </div>

                    <div class="edit__field"> 
                        <label class="edit__field__label" for="periodicity">Remise à zéro tous les </label>
                        <select class="edit__field__input"
                            id="periodicity"
                            v-model="budget.periodicite"
                            >
                            <option :value="null" disabled="disabled">Sélectionner une périodicité</option>
                            <option v-for="periodicity in $setting('BUDGET_PERIODICITY')"
                                :key="periodicity"
                                :value="periodicity">
                                {{ periodicity }}
                            </option>
                        </select>
                        <span class="edit__field__suffix">mois</span>       
                    </div>        

                </fieldset>

                <fieldset class="edit__fieldset">
                    <div class="edit__field" :class="{ 'error': errors.approbateurs }"> 
                        <label class="edit__field__label" for="approbateurs">Approbateurs
                            <span>({{this.selectedApprobateurs.length}}/3)</span>
                        </label>
                        <input-tag css="woocommerce-Input woocommerce-Input--text input-text"
                            v-model="selectedApprobateurs" 
                            :check="(value) => checkApprobateur(value)"
                            :max="3" />
                    </div>
                </fieldset>

                <fieldset class="edit__fieldset">
                    <h3>Utilisateurs
                        <span>({{this.selectedUtilisateurs.length}}/{{utilisateurs.length}})</span>
                    </h3>
                    <ul class="utilisateurs">
                        <li class="utilisateurs__item"
                            v-for="utilisateur in utilisateurs"
                            :key="utilisateur.id">

                            <div class="utilisateur">
                                <div class="utilisateur__name">
                                    <label :for="'cb_utilisateur_' + utilisateur.id">                                        
                                        <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                            type="checkbox"
                                            :id="'cb_utilisateur_' + utilisateur.id"
                                            :value="utilisateur.id"
                                            :disabled="!isUtilisateurEditable"
                                            v-model="selectedUtilisateurs" />
                                        <strong>{{ utilisateur.name }} <span v-if="isCurrentUtilisateur(utilisateur)">(Vous)</span></strong>
                                    </label>
                                </div>
                            </div>

                        </li>
                    </ul>
                </fieldset>

                <fieldset class="edit__fieldset">
                    <h3>Adresses                                                     
                        <span>({{this.selectedClientAddresses.length}}/{{countAdresses}})</span>
                    </h3>
                    
                    <ul class="clients"
                        v-for="client in clients"
                        :key="'client' + client.id">
                        <li class="clients__item">

                            <div class="client">
                            
                                <collapse :hidden="false" :selected="true">

                                    <div slot="collapse-header">
                
                                            <div class="client__name">
                                                <label @click.stop :for="'cb_client_' + client.id">  
                                                    <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                                        type="checkbox"
                                                        :checked="isAllAddressChecked(client)"
                                                        :id="'cb_client_' + client.id" 
                                                        :disabled="!isAddressEditable"
                                                        @input="( $event.target.checked ) ? addAllClientAddress(client) : removeAllClientAddress(client)"/>
                                                    <strong>Client {{ client.code }} — {{ client.name }} ({{getAddressesSelectedMessage(client)}})
                                                    </strong>
                                                </label>
                                            </div>
                                    </div>

                                    <div slot="collapse-body"> 

                                        <!-- Adresses client -->
                                        <ul class="client__addresses">
                                            <li class="client__addresses__item"
                                                v-for="clientAddress in client.clientAddresses"
                                                :id="'clientAddress_' + clientAddress.id"
                                                :key="'clientAddresss' + clientAddress.id">

                                                <div class="client-address"> 
                                                    <div class="client-address__content">       
                                                        <label class="woocommerce-form__label woocommerce-form__label-for-checkbox"
                                                            :for="'cb_clientAddress_' + clientAddress.id">
                                                            <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                                                type="checkbox"
                                                                v-model="selectedClientAddresses"
                                                                :id="'cb_clientAddress_' + clientAddress.id"
                                                                :disabled="!isAddressEditable"
                                                                :value="clientAddress.id"/>   
                                                            <span class="client-address__content__name">                           
                                                                <strong>{{ clientAddress.address.raisonSociale1 }} {{ clientAddress.address.raisonSociale2 }} <span v-if="clientAddress.address.city">({{ clientAddress.address.city }})</span></strong><br />
                                                                <span>{{ clientAddress.address.address1 }} {{ clientAddress.address.address2 }}{{ clientAddress.address.address3 }} {{ clientAddress.address.postalCode }} {{ clientAddress.address.city }}</span><br />
                                                                <small>{{ clientAddress.address.homePhone }}</small>                                
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>

                                            </li>
                                        </ul>

                                    </div>

                                </collapse>   

                            </div>


                        </li>

                    

                    </ul>

                    


                </fieldset>

                <editbar>                               
                    <button class="woocommerce-button button alt"
                        type="submit"
                        value="submit"
                        v-if="isNew && $setting('BUDGET_CREATE_ENABLED')">Créer</button>                    
                    <button class="woocommerce-button button alt"
                        type="submit"
                        value="submit"
                        v-if="!isNew && $setting('BUDGET_UPDATE_ENABLED')">Valider</button>        
                    <router-link class="woocommerce-button button"
                        :to="{ name: 'budgetList' }">Annuler</router-link>
                </editbar>

            </Edit>

        </article>

    </div>
</template>

<script>
import { mapState } from 'vuex'

import { actions } from '@/store/budgets'
import { actions as clientActions } from '@/store/clients'
import { actions as utilisateurActions } from '@/store/utilisateurs'
import title from "@/containers/title";

import Datepicker from 'vuejs-datepicker'
import * as DATEPICKER_LOCALES from "vuejs-datepicker/src/locale"
import Collapse from '@/components/collapse/Collapse.vue'
import Edit from '@/components/edit/Edit.vue'
import Editbar from '@/components/edit/Editbar.vue'
import Loader from '@/components/loader/Loader.vue'
import InputTag from '@/components/inputTag/InputTag.vue'
import Message from '@/components/message/Message.vue';
import { validateEmail } from '@/helpers/validation';


export default {

    name: 'Budget',

    data: function() {
        return {

            datepickerLocale: DATEPICKER_LOCALES['fr'],
            datepickerMondayFirst: true,
            errors: {},
            processing: false,
            ready: false,

            budget: {
                id: null,
                libelle: '',
                amount: 0,
                periodicite: 1,                
                validiteStartAt: null,
                validiteEndAt: null,
            },
            selectedApprobateurs: [],
            selectedClientAddresses: [],
            selectedUtilisateurs: [],

            validateEmail: validateEmail
        }
    },

    computed: {

        isNew: function() { return ( this.ready && this.budget.id === null ) },

        ...mapState({

            clients: state => state.clients.all,

            clientAddresses: state => state.utilisateurs.currentAddresses,

            countAdresses: function() {
                let nbAddresses = 0
                this.clients.forEach ( client => {
                    nbAddresses += client.clientAddresses.length
                 })
                return nbAddresses
            },

            isAddressEditable: function() {

                /// Pour une modification, si l'association budget / adresse n'est pas activée dans les réglages du plugin,
                /// on ne peut pas les modifier
                if ( !this.isNew && !this.$setting('BUDGET_ADDRESS_UPDATE_ENABLED') ) {
                    return false
                }

                /// Pour une création, si l'association budget / adresse n'est pas activée dans les réglages du plugin,
                /// on ne peut pas en ajouter
                if ( this.isNew && !this.$setting('BUDGET_ADDRESS_CREATE_ENABLED') ) {
                    return false
                }

                return true   
            },        

            isUtilisateurEditable: function() {
                
                /// Pour une modification, si l'association budget / utilisateur n'est pas activée dans les réglages du plugin,
                /// on ne peut pas les modifier
                if ( !this.isNew && !this.$setting('BUDGET_UTILISATEUR_UPDATE_ENABLED') ) {
                    return false
                }

                /// Pour une création, si l'association budget / utilisateur n'est pas activée dans les réglages du plugin,
                /// on ne peut pas en ajouter
                if ( this.isNew && !this.$setting('BUDGET_UTILISATEUR_CREATE_ENABLED') ) {
                    return false
                }

                return true  
            },
                        
            utilisateurs: state => state.utilisateurs.all,

            utilisateur: state => state.utilisateurs.current

        })
    },

    methods: {

        addAllClientAddress: function(client) {

            client.clientAddresses.forEach( clientAddress => {
                if ( !this.selectedClientAddresses.includes(clientAddress.id) ) {
                    this.selectedClientAddresses.push(clientAddress.id)
                }
            })
        },

        checkApprobateur: function(approbateur) {
            return validateEmail(approbateur)
                && !this.selectedApprobateurs.includes(approbateur)
        },

        checkForm: function(e) {

            e.preventDefault()
            this.errors = {}
            if ( this.budget.libelle === '' ) {
                this.errors.name = "Le libellé du budget est obligatoire."
            }
            if ( !this.budget.validiteStartAt ) {
                this.errors.validityFrom = "La date de début de validité est obligatoire."
            }            
            if ( !this.budget.validiteEndAt ) {
                this.errors.validityTo = "La date de fin de validité est obligatoire."
            }

            if ( this.selectedApprobateurs.length < 1 ) {
                this.errors.approbateurs = "Au moins un approbateur doit être renseigné."
            }

            if ( Object.values(this.errors).length > 0 ) {
                document.getElementById("top").scrollTo({top:0, behavior: 'smooth'});
                return
            }

            this.processing = true
            this.budget.approbateurs = this.selectedApprobateurs
            this.budget.clientAddressIds = this.selectedClientAddresses
            this.budget.utilisateurIds = this.selectedUtilisateurs

            let action = ( this.isNew ) ? actions.ADD : actions.UPDATE            
            this.$store.dispatch(action, this.budget)
                .then(() => {
                    const successMsg = ( this.isNew ) ? 'Budget créé' : 'Budget modifié'
                    this.$notify.success(successMsg)
                    this.$router.push({ name: "budgetList" })
                })
                .catch((e) => {
                    if ( e.response.status === 422 ) {
                        e.response.data.error.forEach((message, index) => {
                            this.errors[`remote_${index}`] = message
                        })     
                    }
                    else {
                        this.$error(  'Une erreur est survenue' )
                    }
                })
                .finally(() => {
                    this.processing = false
                    if ( Object.values(this.errors).length > 0 ) {           
                        document.getElementById("top").scrollTo({top:0, behavior: 'smooth'});
                    }  
                })
        },

        getAddressesSelectedMessage: function(client) {
            let nbSelectedAddresses = 0
            client.clientAddresses.forEach( clientAddress => {
                    if ( this.isAddressChecked(clientAddress) ) {
                        nbSelectedAddresses++
                    }
                })

            let info = nbSelectedAddresses + " adresse" + (nbSelectedAddresses> 1 ? "s": "") + 
                       " sélectionnée" + (nbSelectedAddresses> 1 ? "s": "") + " sur " +
                       client.clientAddresses.length + " possible" + (client.clientAddresses.length> 1 ? "s ": " ")
            return info
        },

        isAllAddressChecked: function(client) {
            
            let all = true

            if (client.clientAddresses.length == 0) {
                all = false
            }
            else {
                client.clientAddresses.forEach( clientAddress => {
                    if ( !this.isAddressChecked(clientAddress) ) {
                        all = false
                    }
                })
            }
            
            return all
        },

        isAddressChecked: function(clientAddress) {
            
            let checked = false
            this.selectedClientAddresses.forEach(clientAddressId => {
                if ( clientAddressId === clientAddress.id ) {
                    checked = true
                }
            });

            return checked
        },

        isCurrentUtilisateur: function(utilisateur) {
            return ( this.utilisateur.id === utilisateur.id )
        },

        removeAllClientAddress: function(client) {

            client.clientAddresses.forEach( clientAddress => {
                const index = this.selectedClientAddresses.indexOf(clientAddress.id);
                if ( index > -1 ) {
                    this.selectedClientAddresses.splice(index, 1);
                }
            })
        }
    },
  
    created() {

        
        let promises = [
            this.$store.dispatch(clientActions.GET_ALL),
            this.$store.dispatch(utilisateurActions.GET_COLLECTION),
            this.$store.dispatch(utilisateurActions.GET_ADDRESSES)
        ]
        
        this.id = ( this.$route.params.id ) ?? null
        if ( this.id ) {
            promises.push(this.$store.dispatch(actions.GET, this.id))
        }

        Promise.all(promises)
            .then( data => {
                
                if ( data.length > 3 ) {
                    this.budget = { ...data[3] }
                    this.selectedApprobateurs = [ ...this.budget.approbateurs ]
                    this.selectedClientAddresses = [ ...this.budget.clientAddressIds ]
                    this.selectedUtilisateurs = [ ...this.budget.utilisateursIds ]
                }

                this.ready = true

            })            
            .catch(error => {
                this.$errorPage(error.reason)
            })
    },

    components: {
        Collapse,
        Datepicker,
        Edit,
        Editbar,
        InputTag,
        Loader,
        Message
    },

    mixins: [ title ]
}
</script>

<style>

.budget {
    position: relative;
}

.budget .collapse .collapse-header > div {
  display: block;
}


.budget__heading {
    margin-bottom: 2rem;
}

.budget__heading__actions {
    position: absolute;
    right: 0;
    top: 0;
}

.budget__footer {
}

.budget__footer__actions {
    text-align: right;
}

.utilisateurs {
    max-height: 300px;
    overflow: auto;
}
.utilisateurs,
.utilisateurs__item {
    list-style-type: none;
    margin: 0;
}

.utilisateur .utilisateur__name {
    padding: 0.5rem 1rem;
}

.negative {
    color: rgb(255, 48, 0);
}  

</style>
